// The notifications as a drawer (F502, decision 109): the bell opens it
// over the page, the unread ones are listed newest first, a click opens
// the page the notification is about and the notification goes; nothing
// stays to be tidied. Mark all as read empties it. The read ones remain on
// the Fleet's Activity list.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { api, queries, unwrap, type Feed } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { formatRelative } from "@/lib/format";

// One open state for the whole shell: the bell, the desk's "unread" line
// and the drawer itself read and set it.
let open = false;
const listeners = new Set<() => void>();
function setOpen(next: boolean) {
  if (open === next) return;
  open = next;
  for (const l of listeners) l();
}
export function openNotifications() {
  setOpen(true);
}
export function closeNotifications() {
  setOpen(false);
}
export function toggleNotifications() {
  setOpen(!open);
}
export function useNotificationsOpen(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => open,
    () => false,
  );
}

type Item = Feed["items"][number];

export function NotificationsDrawer() {
  const isOpen = useNotificationsOpen();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const feed = useQuery(queries.notifications());
  const panel = useRef<HTMLDivElement | null>(null);
  // The drawer opens under the top bar (decision 125): the bell stays in
  // view and in reach, and a second press closes it.
  const [top, setTop] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    const bar = document.querySelector<HTMLElement>(".topbar");
    const place = () => setTop(bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0);
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen]);
  const markRead = useMutation({
    mutationFn: async (what: string) => unwrap(await api.POST("/api/v1/me/notifications/{target}", { params: { path: { target: `${what}:read` } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  // Escape closes; the focus goes to the panel when it opens and back to
  // the bell when it closes.
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeNotifications();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const unread = (feed.data?.items ?? []).filter((n) => !n.read_at);
  // A click reads the notification and goes where it points; the read one
  // leaves the list at once, before the platform answers.
  const take = (n: Item) => {
    // a refetch in flight would bring the read one back for a moment
    queryClient.cancelQueries({ queryKey: ["notifications"] });
    queryClient.setQueryData<Feed>(queries.notifications().queryKey, (old) =>
      old ? { ...old, unread: Math.max(0, old.unread - 1), items: old.items.map((i) => (i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i)) } : old,
    );
    markRead.mutate(n.id);
    if (n.link) {
      closeNotifications();
      navigate({ to: n.link });
    }
  };
  return (
    <div className="drawer-root" style={{ top }} data-testid="notifications-drawer">
      <button type="button" className="drawer-scrim" aria-label="Close the notifications" onClick={closeNotifications} data-testid="notifications-close" />
      <div className="drawer" role="dialog" aria-modal="true" aria-label="Notifications" tabIndex={-1} ref={panel}>
        <div className="drawer-head">
          <h2 className="text-[1.0714rem] font-semibold">Notifications</h2>
          <span className="muted text-xs">{unread.length === 0 ? "nothing new" : `${unread.length} new`}</span>
          {unread.length > 0 ? (
            <button
              type="button"
              className="btn btn-sm ml-auto"
              onClick={() => {
                queryClient.cancelQueries({ queryKey: ["notifications"] });
                queryClient.setQueryData<Feed>(queries.notifications().queryKey, (old) => (old ? { ...old, unread: 0, items: old.items.map((i) => (i.read_at ? i : { ...i, read_at: new Date().toISOString() })) } : old));
                markRead.mutate("all");
              }}
              disabled={markRead.isPending}
              data-testid="read-all"
            >
              Mark all as read
            </button>
          ) : null}
          <button type="button" className={`iconbtn ${unread.length > 0 ? "" : "ml-auto"}`} aria-label="Close the notifications" title="Close" onClick={closeNotifications} data-testid="notifications-close-x">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <ErrorNote error={feed.error ?? markRead.error} />
        {unread.length === 0 ? (
          <p className="muted px-5 py-6 text-sm" data-testid="notifications-empty">
            Nothing new. Requests, bookings, returns and held returns on your airframes land here; the ones you read stay on the Fleet's Activity list.
          </p>
        ) : (
          <ul className="drawer-list" data-testid="feed">
            {unread.map((n) => (
              <li key={n.id}>
                <button type="button" className="drawer-item" onClick={() => take(n)} data-testid="notification" data-type={n.type} aria-label={n.link ? `${n.title}. Open the page` : `${n.title}. Mark as read`}>
                  <span className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 font-semibold">{n.title}</span>
                    <span className="muted shrink-0 text-xs tnum">{formatRelative(n.created_at)}</span>
                  </span>
                  <span className="muted block text-sm whitespace-pre-line">{n.body}</span>
                  <span className="text-accent block text-xs">{n.link ? "Open the page" : "Got it"}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
